/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useStore } from './stores/common';
import { usePrimitiveStore } from './stores/commonPrimitive';
import * as Selector from './stores/selector';
import 'antd/dist/reset.css';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { setMessage } from './helpers';
import Spinner from './components/spinner';
import MainToolBar from './mainToolBar';
import ExamplesDrawer from './examplesDrawer';
import ProjectListPanel from './project/projectListPanel.tsx';
import { useAuthHandlers } from './useAuthHandlers';
import { useCloudProjects } from './useCloudProjects';
import { useTranslation } from 'react-i18next';
import AnonymousImage from './assets/anonymous.png';
import { app, auth, firestore } from './firebase.ts';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface CloudManagerProps {
  viewOnly: boolean;
}

const useFlag = (flag: boolean, fn: () => void, setFlag: () => void) => {
  useEffect(() => {
    if (flag) {
      fn();
      setFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag]);
};

const CloudManager = React.memo(({ viewOnly = false }: CloudManagerProps) => {
  const setCommonStore = useStore(Selector.set);
  const setPrimitiveStore = usePrimitiveStore(Selector.setPrimitiveStore);
  const language = useStore(Selector.language);
  const waiting = usePrimitiveStore(Selector.waiting);
  const showProjectListPanel = usePrimitiveStore(Selector.showProjectListPanel);
  const createProjectFlag = usePrimitiveStore(Selector.createProjectFlag);
  const saveProjectAsFlag = usePrimitiveStore(Selector.saveProjectAsFlag);
  const saveProjectFlag = usePrimitiveStore(Selector.saveProjectFlag);
  const saveAndThenOpenProjectFlag = usePrimitiveStore(Selector.saveAndThenOpenProjectFlag);
  const openRecentProjectFlag = usePrimitiveStore(Selector.openRecentProjectFlag);
  const showProjectsFlag = usePrimitiveStore(Selector.showProjectsFlag);
  const updateProjectsFlag = usePrimitiveStore(Selector.updateProjectsFlag);
  const shareProjectFlag = usePrimitiveStore(Selector.shareProjectFlag);
  const removeFromCommonsFlag = usePrimitiveStore(Selector.removeFromCommonsFlag);

  const { t } = useTranslation();
  const lang = useMemo(() => ({ lng: language }), [language]);

  const { signIn, signInAnonymously, signOut } = useAuthHandlers();
  const {
    myProjectsRef,
    loadProject,
    createNewProject,
    saveProject,
    saveProjectAs,
    showMyProjectsList,
    deleteProject,
    restoreProject,
    deleteProjectForever,
    emptyTrash,
    fetchMyTrash,
    renameProject,
    publishProject,
    unpublishProject,
    updateProjects,
  } = useCloudProjects();

  // Deep-link (?userid=&project=) bootstrap guard. The load must wait for
  // Firebase Auth to settle — see maybeLoadDeepLink — so it can't run in the
  // synchronous mount path. This ref makes it fire exactly once.
  const deepLinkLoadedRef = useRef(false);

  useFlag(createProjectFlag, createNewProject, () => setPrimitiveStore('createProjectFlag', false));
  useFlag(saveProjectAsFlag, saveProjectAs, () => setPrimitiveStore('saveProjectAsFlag', false));
  useFlag(saveProjectFlag, saveProject, () => setPrimitiveStore('saveProjectFlag', false));
  useFlag(saveAndThenOpenProjectFlag, saveProject, () => setPrimitiveStore('saveAndThenOpenProjectFlag', false));

  // Open a specific recent project chosen from the File menu. The target
  // (owner + title) is parked on the common store; clear it after reading so
  // a later plain save-then-open doesn't accidentally reuse it.
  const openRecentProject = () => {
    const record = useStore.getState().recentProjectToOpen;
    if (!record) return;
    setCommonStore((state) => {
      state.recentProjectToOpen = null;
    });
    loadProject(record.owner, record.title);
  };
  useFlag(openRecentProjectFlag, openRecentProject, () => setPrimitiveStore('openRecentProjectFlag', false));
  useFlag(showProjectsFlag, showMyProjectsList, () => setPrimitiveStore('showProjectsFlag', false));
  useFlag(updateProjectsFlag, updateProjects, () => setPrimitiveStore('updateProjectsFlag', false));

  // Share / remove-from-Commons act on the currently open project — pull
  // the title from the store at fire time rather than capturing it.
  const shareCurrentProject = async () => {
    const title = useStore.getState().projectState.title;
    if (!title) return;
    const ok = await publishProject(title);
    if (ok) setMessage('success', t('projectListPanel.ProjectShared', lang) + '.');
  };
  const removeCurrentFromCommons = async () => {
    const title = useStore.getState().projectState.title;
    if (!title) return;
    const ok = await unpublishProject(title);
    if (ok) setMessage('success', t('projectListPanel.ProjectRemovedFromCommons', lang) + '.');
  };
  useFlag(shareProjectFlag, shareCurrentProject, () => setPrimitiveStore('shareProjectFlag', false));
  useFlag(removeFromCommonsFlag, removeCurrentFromCommons, () => setPrimitiveStore('removeFromCommonsFlag', false));

  useEffect(() => {
    // Reconcile projectState.isPublic against the publicProjects index
    // whenever this tab becomes visible. Catches the case where the user
    // shared / unshared from the Commons tab (or another device) while
    // this tab held a stale flag — without it the Project menu sticks
    // on "Remove from the Commons" after the entry has already been
    // deleted elsewhere. One lightweight read per visibility change.
    const reconcile = async () => {
      if (document.visibilityState !== 'visible') return;
      const title = useStore.getState().projectState.title;
      const uid = useStore.getState().user.uid;
      if (!title || !uid) return;
      try {
        const snap = await getDoc(doc(firestore, 'publicProjects', `${uid}_${title}`));
        const existsRemotely = snap.exists();
        const currentlyMarked = useStore.getState().projectState.isPublic === true;
        if (existsRemotely !== currentlyMarked) {
          setCommonStore((state) => {
            state.projectState.isPublic = existsRemotely;
          });
        }
      } catch (e) {
        console.warn('isPublic reconcile failed', e);
      }
    };
    document.addEventListener('visibilitychange', reconcile);
    return () => document.removeEventListener('visibilitychange', reconcile);
  }, [setCommonStore]);

  useEffect(() => {
    // Track sign-in status via observer rather than reading auth.currentUser,
    // which can be null while the auth object is still initializing.
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (u) {
        // Record the user's local UTC offset so the daily rate-limit window
        // resets at their LOCAL midnight (the limiter is server-side and can't
        // know the timezone otherwise). getTimezoneOffset() is minutes to ADD to
        // local to reach UTC, so negate it to get minutes-to-add-to-UTC. Fire-
        // and-forget; on failure the window simply stays anchored to UTC.
        try {
          void httpsCallable(
            getFunctions(app, 'us-east4'),
            'setTimeZoneOffset',
          )({ tzOffsetMinutes: -new Date().getTimezoneOffset() }).catch(() => {});
        } catch {
          /* ignore */
        }
      }
      setCommonStore((state) => {
        if (!state.user) return;
        if (u) {
          state.user.uid = u.uid;
          state.user.displayName = u.displayName ?? 'Anonymous';
          state.user.email = u.email;
          state.user.photoURL = u.photoURL ?? AnonymousImage;
          // Recent Projects only keeps the user's own projects. Prune any
          // foreign entries that a shared-link open recorded before that rule
          // existed; now that we know the uid, this cleans up legacy lists.
          state.recentlyOpenedProjects = (state.recentlyOpenedProjects ?? []).filter((p) => p.owner === u.uid);
        } else {
          // Firebase signals no signed-in user (token expired, signed out
          // in another tab, etc.). Clear the persisted uid so saveProject's
          // precheck fails fast instead of writing with a stale uid that
          // Firestore's auth.uid-equality rule will reject.
          state.user.uid = null;
          state.user.email = null;
          state.user.displayName = null;
          state.user.photoURL = null;
        }
      });
      // Auth has now resolved (to a user or to null) — either way it is safe to
      // bootstrap a ?userid=&project= deep link, since shared reads need no token.
      maybeLoadDeepLink();
    });
    init();
    window.addEventListener('popstate', handlePopStateEvent);
    return () => {
      window.removeEventListener('popstate', handlePopStateEvent);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePopStateEvent = () => {
    if (viewOnly) return;
    const p = new URLSearchParams(window.location.search);
    const userid = p.get('userid');
    const project = p.get('project');
    if (userid && project) {
      // TODO: Not sure if we should change the browser's URL address
    }
  };

  const init = () => {
    // NB: the deep-link project load is NOT done here. Running it synchronously
    // on mount fires the Firestore read before onAuthStateChanged has restored
    // the session, so it goes out unauthenticated and is denied intermittently
    // ("sometimes the link doesn't open"). It now runs from maybeLoadDeepLink,
    // gated on the first auth-state resolution.
    fetchLatestVersion().then(() => {
      // ignore
    });
  };

  // Open the ?userid=&project= deep-linked project once auth has settled. Called
  // from the onAuthStateChanged observer, which is what fixes the original race:
  // running the read synchronously on mount fired it before the SDK had restored
  // the session ("sometimes the link doesn't open"). Waiting for the FIRST auth
  // resolution is the whole fix — whether that resolution carries a user or null
  // does not matter. Runs at most once (deepLinkLoadedRef).
  //
  // A SIGNED-OUT VISITOR IS NOT SIGNED IN ANONYMOUSLY HERE, ON PURPOSE.
  // This used to kick off a one-time anonymous sign-in, on the belief that the
  // shared doc needed an authenticated reader. It does not: firestore.rules
  // opens reads at every path (`match /{document=**} { allow read: if true }`)
  // and storage.rules does the same for thumbnails, so the project renders fine
  // with no account at all — verified against the live database with neither an
  // auth token nor an App Check token (HTTP 200).
  //
  // That sign-in was minting a Firebase Auth account for every passive fetch of
  // a shared link — crawlers, link-preview bots, scrapers — and the 2026-09-01
  // abuse audit found ~1/day of exactly those zero-footprint accounts. It also
  // bought a human nothing: on someone else's project isForeignProject() blocks
  // AI regardless (see aiSignInGate.ts), so the auto-account granted a visitor
  // no capability they lack while signed out. Anonymous sign-in remains fully
  // supported and is still how students use AIMS — it now happens when the
  // visitor DOES something that needs an account (Save As, like, join a class),
  // through the existing terms gate in mainToolBar, rather than on arrival.
  const maybeLoadDeepLink = () => {
    if (deepLinkLoadedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const userid = params.get('userid');
    const project = params.get('project');
    if (!userid || !project) return;
    deepLinkLoadedRef.current = true;
    loadProject(userid, project);
  };

  const fetchLatestVersion = async () => {
    try {
      const infoRef = doc(firestore, 'app', 'info');
      const infoSnap = await getDoc(infoRef);

      if (infoSnap.exists()) {
        const data = infoSnap.data();
        if (data?.latestVersion) {
          usePrimitiveStore.getState().set((state) => {
            state.latestVersion = data.latestVersion;
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch latest version:', error);
    }
  };

  return viewOnly ? (
    <>{waiting && <Spinner size={'large'} />}</>
  ) : (
    <>
      {waiting && <Spinner size={'large'} />}
      <MainToolBar signIn={signIn} signInAnonymously={signInAnonymously} signOut={signOut} />
      <ExamplesDrawer viewOnly={viewOnly} />
      {showProjectListPanel && myProjectsRef.current && (
        <ProjectListPanel
          projects={[...myProjectsRef.current]}
          loadProject={loadProject}
          deleteProject={deleteProject}
          restoreProject={restoreProject}
          deleteProjectForever={deleteProjectForever}
          emptyTrash={emptyTrash}
          fetchMyTrash={fetchMyTrash}
          renameProject={renameProject}
          publishProject={publishProject}
          unpublishProject={unpublishProject}
        />
      )}
    </>
  );
});

export default CloudManager;
